import Config from '@/config'
import { hashTextToSHA256 } from '@/utils/tracking'
import * as financeUtils from '@/utils/finance'

const getCheckoutVariant = (rootGetters) => {
  const isSimpleCheckout = rootGetters['configuration/isSimpleCheckoutEnabled']
  const isSingleCheckout = rootGetters['session/singleCheckoutEnabled']

  if (isSimpleCheckout) {
    return 'simple-checkout'
  } else if (isSingleCheckout) {
    return 'single-checkout'
  } else {
    return 'standalone-checkout'
  }
}

const getPhone = (rootGetters) => {
  return hashTextToSHA256(
    rootGetters['auth/customerPhone']?.countryCode +
      rootGetters['auth/customerPhone']?.phoneNumber ||
      rootGetters['session/phoneNumberInternational']
  )
}

const DATA_PARAMS_MAPPINGS = {
  order_id: ({ rootGetters }) => rootGetters['session/orderId'],
  session_id: ({ rootGetters }) => rootGetters.checkoutId,
  user_id: ({ rootGetters }) => rootGetters['auth/customerId'],
  merchant_id: ({ rootGetters }) => rootGetters['session/merchantId'],
  merchant_name: ({ rootGetters }) => rootGetters['session/merchantName'],
  language: ({ rootGetters }) => rootGetters['application/currentLang'],
  order_country_code: ({ rootGetters }) =>
    rootGetters['session/merchantCountry'],
  order_total_amount: ({ rootGetters }) =>
    rootGetters['session/orderTotalAmount']?.amount,
  currency_code: ({ rootGetters }) => rootGetters['session/orderCurrency'],
  id_verified: ({ rootGetters }) => rootGetters['auth/isIdVerified'],
  checkout_step: ({ rootGetters }) => rootGetters['checkout/currentStep'],
  checkout_variation: ({ rootGetters }) => getCheckoutVariant(rootGetters),
  mobile_number: ({ rootGetters }) => hashTextToSHA256(getPhone(rootGetters)),
  apple_pay_preference: ({ rootGetters }) =>
    rootGetters['payment/applePayPreferenceOriginal'],
  apple_pay_preference_from_cards_v2: ({ rootGetters }) =>
    rootGetters['configuration/shouldConsiderApplePayPreference'],
  equal_installment: ({ rootGetters }) =>
    financeUtils.novaIsInProgress(window, rootGetters.checkoutId)
      ? true // Finance is always equal instalments, therefore TRUE here
      : rootGetters['checkout/orderHasEqualInstallments'],
  payment_type: ({ rootGetters }) => {
    if (financeUtils.novaIsInProgress(window, rootGetters.checkoutId)) {
      const cachedFinanceData = financeUtils.getCachedFinanceData(
        window,
        rootGetters.checkoutId
      )

      return `split_` + (cachedFinanceData?.selectedFinanceInstalment ?? 0)
    }

    if (rootGetters['session/isPaylaterOrder']) {
      return 'pay_later'
    }

    if (rootGetters['session/isPayNextMonthOrder']) {
      return 'pay_next_month'
    }

    if (rootGetters['session/isPayNowOrder']) {
      return 'pay_now'
    }

    return `split_${rootGetters['session/numberOfInstallment']}`
  },
  user_type: ({ rootGetters }) => rootGetters['auth/customerType'],
  is_pcidss_enabled: ({ rootGetters }) =>
    rootGetters['configuration/isPcidssEnabled'],
  is_pcidss_apple_pay_enabled: ({ rootGetters }) =>
    rootGetters['configuration/isPcidssApplePayEnabled'],
  product_flow: ({ state }) => state.product_flow,
  is_embedded: ({ rootGetters }) => rootGetters['application/isEmbedded'],
}

const state = {
  order_id: '',
  session_id: '',
  user_id: '',
  merchant_id: '',
  merchant_name: '',
  language: '',
  order_country_code: '',
  order_total_amount: '',
  currency_code: '',
  checkout_variation: '',
  checkout_step: '',
  mobile_number: '',
  id_verified: '',
  equal_installment: '',
  payment_type: '',
  user_type: '',
  apple_pay_preference: false,
  apple_pay_preference_from_cards_v2: false,
  is_pcidss_enabled: false,
  is_pcidss_apple_pay_enabled: false,
  product_flow: 'default',
  is_embedded: false,
}

const getters = {
  getParameters: (state, getters, rootState, rootGetters) => (keys) => {
    return (keys || []).reduce((rs, key) => {
      const getterFn = DATA_PARAMS_MAPPINGS[key] ?? ''
      const value = TypeChecker.isFunction(getterFn)
        ? getterFn({ state, getters, rootState, rootGetters })
        : getterFn

      return { ...rs, [key]: value }
    }, {})
  },
}

const mutations = {
  UPDATE_PARAMETERS(state, parameters = {}) {
    // XXX should we check key in initial state first?
    Object.entries(parameters).forEach(([key, value]) => {
      if (Config.isDev && !(key in state)) {
        console.error(
          `[Tracking Plugin] :: unknown key ${key} while updating tracking params`
        )
      }
      state[key] = value
    })
  },
}

const actions = {
  updateParameters({ commit }, parameters) {
    commit('UPDATE_PARAMETERS', parameters)
  },
}

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
}
